import { FC, FormEvent, ReactElement, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";

import { Checkbox, TextInput } from "@/components";
import { useSnackbar } from "@/hooks";
import { useLoginMutation, useVerifyLoginCodeMutation, useResendVerificationCodeMutation } from "@/services";
import { app_logo_full } from "@/constants";
import { isValidEmail } from "@/utils/form-validation";
import { tokens } from "@/tokens";
import { trackLoginSuccess, trackLoginFailure } from "@/analytics/posthog-events";
import { useAppSelector, useAppDispatch } from "@/store";
import { clearChallenge } from "@/services/auth/auth-slice";

const Login: FC = (): ReactElement => {
  const [login, { isLoading: loginLoading }] = useLoginMutation();
  const [verifyCode, { isLoading: verifyLoading, reset: resetVerify }] = useVerifyLoginCodeMutation();
  const [resendCode, { isLoading: resendLoading, reset: resetResend }] = useResendVerificationCodeMutation();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { show } = useSnackbar();
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [remember, setRemember] = useState<boolean>(true);

  // 2FA code input state
  const { challengeSession } = useAppSelector((state) => state.auth);
  const [codeDigits, setCodeDigits] = useState<string[]>(["", "", "", "", "", ""]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Resend cooldown timer
  const [resendCooldown, setResendCooldown] = useState<number>(30);

  const loading = loginLoading || verifyLoading || resendLoading;

  // Focus first code input and start cooldown when challenge appears
  useEffect(() => {
    if (challengeSession) {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
      setResendCooldown(30);
    }
  }, [challengeSession]);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isValidEmail(email)) {
      show("Invalid email address!", "Please enter a valid email.", "error");
      return;
    }
    login({ email, password, rememberDevice: remember })
      .unwrap()
      .then((data) => {
        if (data.accessToken && data.challenge !== "EMAIL_VERIFICATION") {
          trackLoginSuccess();
        } else if (data.challenge && data.challenge !== "EMAIL_VERIFICATION") {
          show(
            "Action required",
            data.challenge === "NEW_PASSWORD_REQUIRED"
              ? "Please set your password using the link in your welcome email."
              : `Authentication challenge: ${data.challenge}`,
            "error"
          );
        }
      })
      .catch((error) => {
        trackLoginFailure(error.message || "unknown");
        show("Invalid email or password!", error.message, "error");
      });
  };

  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, "").slice(-1);
    const newDigits = [...codeDigits];
    newDigits[index] = digit;
    setCodeDigits(newDigits);

    // Auto-advance to next input
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !codeDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length > 0) {
      const newDigits = [...codeDigits];
      for (let i = 0; i < 6; i++) {
        newDigits[i] = pasted[i] || "";
      }
      setCodeDigits(newDigits);
      // Focus the next empty input or the last one
      const nextEmpty = newDigits.findIndex((d) => !d);
      inputRefs.current[nextEmpty === -1 ? 5 : nextEmpty]?.focus();
    }
  };

  const handleVerifyCode = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const code = codeDigits.join("");
    if (code.length !== 6 || !challengeSession) return;

    verifyCode({ code, challengeSession, rememberDevice: remember })
      .unwrap()
      .then(() => {
        trackLoginSuccess();
      })
      .catch((error) => {
        show("Verification failed", error.message, "error");
        setCodeDigits(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      });
  };

  const handleResendCode = () => {
    if (!challengeSession || resendCooldown > 0) return;
    resendCode({ challengeSession })
      .unwrap()
      .then(() => {
        show("Code sent", "A new verification code has been sent to your email.", "success");
        setResendCooldown(30);
        setCodeDigits(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      })
      .catch((error) => {
        show("Resend failed", error.message, "error");
      });
  };

  const handleBackToLogin = () => {
    resetVerify();
    resetResend();
    dispatch(clearChallenge());
    setCodeDigits(["", "", "", "", "", ""]);
  };

  // 2FA Code Verification Screen
  if (challengeSession) {
    return (
      <Stack
        direction={"column"}
        justifyContent={"center"}
        alignItems={"center"}
        height={"100%"}
      >
        <Box sx={{ width: { xs: "95%", sm: "70%" }, p: { xs: 2, md: 0 } }}>
          <form onSubmit={handleVerifyCode}>
            <Stack spacing={1}>
              <Box>
                <Box sx={{ display: { xs: "flex", lg: "none" } }} mb={1.5}>
                  <img src={app_logo_full} alt="" width={98} />
                </Box>
                <Typography
                  fontSize={tokens.fontSize.lg}
                  sx={{ color: tokens.text.secondary }}
                >
                  Check your email
                </Typography>
              </Box>
            </Stack>
            <Box sx={{ my: 2 }}>
              <Typography
                variant="body2"
                sx={{ color: tokens.text.secondary, mb: 3 }}
              >
                We sent a 6-digit verification code to <strong>{email}</strong>.
                Enter it below to sign in.
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center">
                {codeDigits.map((digit, index) => (
                  <Box
                    key={index}
                    component="input"
                    ref={(el: HTMLInputElement | null) => {
                      inputRefs.current[index] = el;
                    }}
                    value={digit}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleCodeChange(index, e.target.value)
                    }
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                      handleCodeKeyDown(index, e)
                    }
                    onPaste={handleCodePaste}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    sx={{
                      width: 48,
                      height: 56,
                      textAlign: "center",
                      fontSize: "1.5rem",
                      fontWeight: 600,
                      border: "2px solid",
                      borderColor: digit ? "primary.main" : "grey.300",
                      borderRadius: 1,
                      outline: "none",
                      "&:focus": {
                        borderColor: "primary.main",
                        boxShadow: "0 0 0 2px rgba(11, 73, 75, 0.2)",
                      },
                    }}
                  />
                ))}
              </Stack>
            </Box>
            <Button
              variant="contained"
              fullWidth
              sx={{
                mt: 2,
                py: 1.5,
                fontSize: tokens.fontSize.xl,
                fontWeight: tokens.fontWeight.semibold,
              }}
              disabled={codeDigits.join("").length !== 6 || loading}
              type="submit"
              color="primary"
              startIcon={loading ? <CircularProgress size={22} /> : null}
            >
              {loading ? "Verifying" : "Verify Code"}
            </Button>
            <Button
              variant="text"
              fullWidth
              sx={{ mt: 1 }}
              onClick={handleResendCode}
              disabled={resendCooldown > 0 || resendLoading || loading}
            >
              {resendLoading
                ? "Sending..."
                : resendCooldown > 0
                  ? `Resend code (${resendCooldown}s)`
                  : "Resend code"}
            </Button>
            <Button
              variant="text"
              fullWidth
              sx={{ mt: 0.5 }}
              onClick={handleBackToLogin}
              disabled={loading}
            >
              Back to login
            </Button>
          </form>
        </Box>
      </Stack>
    );
  }

  // Standard Login Screen
  return (
    <Stack
      direction={"column"}
      justifyContent={"center"}
      alignItems={"center"}
      height={"100%"}
    >
      <Box sx={{ width: { xs: "95%", sm: "70%" }, p: { xs: 2, md: 0 } }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={1}>
            <Box>
              <Box sx={{display:{xs:"flex",lg:"none"}}} mb={1.5}>
                <img src={app_logo_full} alt="" width={98} />
              </Box>
              <Typography fontSize={tokens.fontSize.lg} sx={{ color: tokens.text.secondary }}>
                Sign in to your account
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ my: 2 }}>
            <Stack spacing={2}>
              <Box>
                <TextInput
                  label="Email"
                  type="email"
                  placeholder="hi@example.com"
                  color="success"
                  error={false}
                  helperText={""}
                  value={email}
                  handleChangeValue={(val: string) => setEmail(val)}
                  inputProps={{ "data-testid": "login-email" }}
                />
              </Box>
              <Box>
                <TextInput
                  label="Password"
                  placeholder="*************"
                  color="success"
                  type={"password"}
                  error={false}
                  helperText={""}
                  value={password}
                  handleChangeValue={(val: string) => setPassword(val)}
                  inputProps={{ "data-testid": "login-password" }}
                />
              </Box>
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                mt={3}
              >
                <Checkbox
                  label="Remember Me"
                  color="primary"
                  checked={remember}
                  onChange={() => setRemember(!remember)}
                />
                <Typography
                  variant="subtitle2"
                  fontSize={tokens.fontSize.lg}
                  color="primary"
                  sx={{ cursor: "pointer", fontWeight: tokens.fontWeight.semibold }}
                  onClick={() => navigate("/forgot-password")}
                >
                  Forgot Password
                </Typography>
              </Stack>
            </Stack>
          </Box>
          <Button
            variant="contained"
            fullWidth
            sx={{
              mt: 2,
              py: 1.5,
              fontSize: tokens.fontSize.xl,
              fontWeight: tokens.fontWeight.semibold,
            }}
            disabled={!isValidEmail(email) || password === "" || loading}
            type="submit"
            color="primary"
            startIcon={loading ? <CircularProgress size={22} /> : null}
            data-testid="login-submit"
          >
            {loading ? "Loading" : "Login"}
          </Button>
        </form>
      </Box>
    </Stack>
  );
};

export default Login;
